import { useEffect } from "react";
import { genders, titles } from "./consts";
import { toast } from "react-toastify";
import { download_Graph, content_copy_Graph } from "../../src/assets/images/images"
import { processLatex } from "./PreProcessorLatexCode";

function getKeyByValue(dictionary, value) {
  const key = Object.keys(dictionary).find((key) => dictionary[key] === value);
  return key || null; // Return null if the value is not found in the dictionary
}
function getKeyByDictionary(dictionary, selectedDictionary) {
  let selectedKey;
  for (const [key] of Object.entries(dictionary)) {
    if (key === Object.keys(selectedDictionary)[0]) {
      selectedKey = key;
    } else {
      selectedKey = Object.keys(dictionary)[0];
    }
  }
  return selectedKey;
}
const getTitle = (titleValue, name) => {
  const titleObj = titles.find((title) => `${title.value}` === `${titleValue}`);
  if (titleObj) {
    return `${titleObj.name} ${name}`;
  }
  return name || "";
};
const truncate = (str, num) => {
  if (str.length > num) {
    return str.slice(0, num) + "...";
  } else {
    return str;
  }
};
function getIdsForLanguages(languageArray, languageObject) {
  const languageIds = languageArray.map((lang) => {
    const id = Object.keys(languageObject).find(
      (key) => languageObject[key] === lang
    );
    return id || null; // Return null if the language is not found in the object
  });
  return languageIds.join(","); // Join the IDs with a comma
}

function isLinkOrText(input) {
  if (input.includes("http")) {
    return true;
  } else {
    return false;
  }
}

function capitalizeFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function capitalizeWordsInList(wordList) {
  return wordList.map(function (sentence) {
    return sentence
      .split(" ")
      .map(function (word) {
        return capitalizeFirstLetter(word);
      })
      .join(" ");
  });
}

// Capitalize the first letter of every word (e.g. "web development" -> "Web Development").
// Non-string values are returned unchanged so callers can pass optional fields safely.
function toTitleCase(str) {
  return typeof str === "string"
    ? str.replace(/\b\w/g, (char) => char.toUpperCase())
    : str;
}

// A course's free_domains lists email domains that get the course at no cost.
// It is saved as a JSON string on create, so accept either shape.
function parseFreeDomains(course) {
  const raw = course?.free_domains;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [raw];
    } catch {
      return [raw];
    }
  }
  return [];
}

// True when the student's email domain is whitelisted on the course, meaning
// they take the enroll/request flow rather than the cart flow.
// Comparison is trimmed, lowercased and tolerant of a leading "@".
function hasFreeDomainAccess(course, email) {
  const domain = (email || "").split("@")[1]?.trim().toLowerCase();
  if (!domain) return false;

  return parseFreeDomains(course).some(
    (entry) => String(entry).trim().toLowerCase().replace(/^@/, "") === domain,
  );
}

function binaryStringToArrayBuffer(binary) {
  const len = binary.length;
  const buffer = new ArrayBuffer(len);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < len; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}
function changeScrollPopup(value, className) {
  const popupContainer = document.querySelector(`.${className}`);

  if (popupContainer) {
    popupContainer.style.overflow = value;
    if (value === "hidden") {
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.position = "";
      document.body.style.width = "";
    }
  }
}

const convertSecondsToMinutes = (seconds) => {
  if (!seconds) {
    return "0 min 1 sec";
  }
  if (seconds === "0") {
    return "-";
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes} min ${remainingSeconds} sec`;
};

function timeDifferenceInMinutes(timestamp1, timestamp2) {
  var date1 = new Date(timestamp1);
  var date2 = new Date(timestamp2);

  var difference = date2 - date1;
  var minutes = Math.abs(Math.floor(difference / 60000)); // Use Math.abs for absolute value

  // Handle pluralization
  var minuteText = minutes === 1 ? "min" : "mins";

  return `${minutes} ${minuteText}`; // Returns the formatted string
}

function isOthersGender(selGender) {
  return (
    genders.find((gender) => `${gender.value}` === `${selGender}`) !==
    undefined &&
    (`${selGender}` === "0" || `${selGender}` === "3")
  );
}
function useOutsideClick(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

const generateIdFromHeader = (header) => {
  // nbsp is non-ASCII, so normalise it first — otherwise a Latin header that
  // contains one would be treated as non-Latin.
  const raw = String(header ?? "").replace(/ /g, " ").trim();
  if (!raw) return "";

  if (/[^\x00-\x7F]/.test(raw)) {
    return raw.replace(/\s+/g, "-");
  }

  return raw.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
};

const addIdsToParagraphs = (description, lessonJson) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(description, 'text/html');
  const paragraphs = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p');

  // Normalize text for accurate matching: convert nbsp to a normal space and
  // collapse all internal whitespace so parsed HTML (which often contains
  // &nbsp;/newlines/double spaces) still matches the plain section header.
  const normalizeText = (text) =>
    text?.replace(/ /g, " ").replace(/\s+/g, " ").trim().toLowerCase() ?? "";

  paragraphs.forEach((p) => {
    const pText = normalizeText(p.textContent);

    lessonJson.forEach((section) => {
      const headerText = normalizeText(section.section_header);

      // Skip empty headers — `pText.includes("")` is always true and would
      // stamp an (empty) id onto every paragraph.
      if (!headerText) return;

      if (pText.includes(headerText)) {
        // Use the section_header value as an ID, transforming it into a valid HTML ID format
        const id = generateIdFromHeader(section.section_header);
        if (id) p.id = id;
      }
    });
  });

  // Return the modified HTML as a string
  return doc.body.innerHTML;
};


const _floatingTooltip = (() => {
  let el = null;
  let hideTimeout = null;

  function ensure() {
    if (!el) {
      el = document.createElement("div");
      el.className = "floating-tooltip";
      Object.assign(el.style, {
        position: "fixed",
        zIndex: 99999,
        pointerEvents: "none",
        padding: "6px 10px",
        background: "rgba(0,0,0,0.85)",
        color: "white",
        borderRadius: "6px",
        fontSize: "12px",
        whiteSpace: "nowrap",
        transform: "translateY(-6px)",
        transition: "opacity 0.12s ease, transform 0.12s ease",
        opacity: "0",
      });
      document.body.appendChild(el);
    }
    return el;
  }

  function show(text, rect) {
    const node = ensure();
    node.textContent = text;
    const x = rect.left + rect.width / 2;
    const y = rect.top;
    const { width: tw, height: th } = node.getBoundingClientRect();
    let left = Math.round(x - tw / 2);
    let top = Math.round(y - th - 10);
    const vw = window.innerWidth;
    left = Math.max(8, Math.min(left, vw - tw - 8));
    if (top < 8) top = rect.bottom + 10;
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
    requestAnimationFrame(() => {
      node.style.opacity = "1";
      node.style.transform = "translateY(0)";
    });
    if (hideTimeout) clearTimeout(hideTimeout);
  }

  function hide(delay = 50) {
    const node = ensure();
    if (hideTimeout) clearTimeout(hideTimeout);
    hideTimeout = setTimeout(() => {
      node.style.opacity = "0";
      node.style.transform = "translateY(-6px)";
    }, delay);
  }

  return { show, hide };
})();




// ✅ Copy HTML string to clipboard
const copyToClipboard = (html) => {
  const textArea = document.createElement("textarea");
  textArea.value = html;
  document.body.appendChild(textArea);
  textArea.select();



  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(textArea.value)
        .then(() => toast.success("Copied to clipboard ✅", { position: "top-center" }))
        .catch(() => toast.error("Failed to copy ❌", { position: "top-center" }));
    } else {
      document.execCommand("copy");
      toast.success("Copied to clipboard ✅", { position: "top-center" });
    }
  } catch (error) {
    toast.error("Failed to copy ❌", { position: "top-center" });
  } finally {
    document.body.removeChild(textArea);
  }
};


// ✅ Create an icon button
// const createIconButton = ({ iconSrc, alt, size = 25, onClick }) => {
//   const btn = document.createElement("button");
//   btn.style.background = "transparent";
//   btn.style.border = "none";
//   btn.style.cursor = "pointer";
//   btn.style.padding = "2px";
//   btn.style.display = "flex";
//   btn.style.alignItems = "center";
//   btn.style.justifyContent = "center";

//   const icon = document.createElement("img");
//   icon.src = iconSrc;
//   icon.alt = alt;
//   icon.style.width = `${size}px`;
//   icon.style.height = `${size}px`;

//   btn.appendChild(icon);
//   btn.addEventListener("click", onClick);

//   return btn;
// };




const createIconButton = ({ iconSrc, alt, size = 25, onClick, title }) => {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.style.background = "transparent";
  btn.style.border = "none";
  btn.style.cursor = "pointer";
  btn.style.padding = "2px";
  btn.style.display = "inline-flex";
  btn.style.alignItems = "center";
  btn.style.justifyContent = "center";

  if (title) btn.title = title; // <-- native tooltip

  const icon = document.createElement("img");
  icon.src = iconSrc;
  icon.alt = alt || "";
  icon.style.width = `${size}px`;
  icon.style.height = `${size}px`;
  icon.style.pointerEvents = "none";

  btn.appendChild(icon);
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);
  });

  return btn;
};

// Re-encode any decodable image source to PNG — Chromium only allows
// image/png on clipboard writes, so JPEG/WebP must be converted.
// Accepts a Blob or an already-loaded HTMLImageElement.
const reencodeToPng = async (blob) => {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (out) => (out ? resolve(out) : reject(new Error("toBlob returned null"))),
      "image/png",
    );
  });
};


const getPngBlob = async (src) => {
  const res = await fetch(src, { mode: "cors", credentials: "omit" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return blob.type === "image/png" ? blob : reencodeToPng(blob);
};


// Attributes that should survive a copy → paste round trip.
// Deliberately excludes data-enhanced (runtime marker) and style
// (re-applied by the renderer), so the pasted image gets enhanced again.
const COPYABLE_IMG_ATTRS = ["class", "question-id"];

// Classes added purely by the viewer wrapper shouldn't leak into the markup.
const NON_PORTABLE_IMG_CLASSES = new Set(["img-wrapper"]);

const getCopyableImageAttrs = (img) => {
  if (!img?.getAttribute) return {};

  return COPYABLE_IMG_ATTRS.reduce((acc, name) => {
    let value = img.getAttribute(name);
    if (!value) return acc;

    if (name === "class") {
      value = value
        .split(/\s+/)
        .filter((c) => c && !NON_PORTABLE_IMG_CLASSES.has(c))
        .join(" ");
      if (!value) return acc;
    }

    acc[name] = value;
    return acc;
  }, {});
};


const buildImageMarkup = (src, alt = "", attrs = {}) => {
  const escapeAttr = (v) => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;");

  const extra = Object.entries(attrs)
    .filter(([k, v]) => k && v !== undefined && v !== null && String(v).trim() !== "")
    .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
    .join("");

  return `<p class="editor-paragraph"><br></p><img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}"${extra} />`;
};




/**
 * Copies the actual image bytes to the clipboard as image/png, so it pastes as
 * a picture in WhatsApp, Google Docs, Slack, Word, etc. instead of a raw URL.
 * Falls back to copying the link if the image can't be read.
 */

// Handoff for in-app copy → paste: the pasted PNG carries no URL, so remember
// what the copy button put on the clipboard and match it back on paste.


const copyImageToClipboard = (src, alt = "", attrs = {}) => {
  if (!src) return;
  const markup = buildImageMarkup(src, alt, attrs);

  const canWriteRich =
    typeof window !== "undefined" &&
    typeof window.ClipboardItem !== "undefined" &&
    typeof navigator.clipboard?.write === "function" &&
    window.isSecureContext;

  if (!canWriteRich) {
    copyToClipboard(markup);
    return;
  }

  const pngPromise = getPngBlob(src);

  navigator.clipboard
    .write([
      new ClipboardItem({
        "image/png": pngPromise,
        "text/html": new Blob([markup], { type: "text/html" }),
        "text/plain": new Blob([markup], { type: "text/plain" }),
      }),
    ])
    .then(() => toast.success("Image copied ✅", { position: "top-center" }))
    .catch((err) => {
      // Usually CORS/403 on the image fetch. A rejected image/png promise
      // rejects the whole write, so nothing landed on the clipboard. Retry
      // without the PNG: this item needs no network, and keeping text/html on
      // the clipboard is what lets the paste handlers rebuild the <img> with
      // its attributes instead of dropping to the plain-text branch.
      console.error("Rich image copy failed, retrying without PNG:", err);
      return navigator.clipboard
        .write([
          new ClipboardItem({
            "text/html": new Blob([markup], { type: "text/html" }),
            "text/plain": new Blob([markup], { type: "text/plain" }),
          }),
        ])
        .then(() => toast.success("Image copied ✅", { position: "top-center" }))
        .catch((err2) => {
          // Second failure is usually lost transient activation — last resort,
          // writes text/plain only.
          console.error("Markup clipboard write failed:", err2);
          copyToClipboard(markup);
        });
    });
};





// "Graph 1" + https://.../plot.svg?sig=... -> "graph_1.svg"
const buildImageFilename = (src, alt = "") => {
  let ext = "png";
  try {
    const pathname = new URL(src, window.location.href).pathname || "";
    const match = pathname.match(/\.([a-z0-9]+)$/i);
    if (match) ext = match[1].toLowerCase();
  } catch {
    // keep the png default — a malformed src still downloads with a sane name
  }

  const base =
    (alt || "image")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-") // characters Windows rejects in filenames
      .replace(/\s+/g, "_") || "image";

  return base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
};

/**
 * Downloads the image instead of navigating to it.
 *
 * <a download> is ignored for cross-origin URLs, so the plain anchor approach
 * just replaces the current page with the image. Fetching into a blob: URL
 * makes the href same-origin, which the browser will honour. If the fetch is
 * blocked (CORS/403/expired signed URL) we open a new tab rather than losing
 * the page the user is on.
 */
const fetchImageBlob = async (src, cache) => {
  const res = await fetch(src, { mode: "cors", credentials: "omit", cache });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};

const downloadImage = async (src, alt = "") => {
  if (!src) return;

  const filename = buildImageFilename(src, alt);

  let blob;
  try {
    blob = await fetchImageBlob(src, "default");
  } catch (err) {
    // The <img> already fetched this URL without an Origin header, and the
    // browser can hand that cached, header-less response back to our CORS
    // fetch — which then fails even though the bucket is configured correctly.
    // Retry bypassing the cache so the request actually goes out with Origin.
    console.warn("Image fetch failed, retrying without cache:", err);
    try {
      blob = await fetchImageBlob(src, "reload");
    } catch (err2) {
      // Genuinely unreachable (CORS not configured / 403 / expired signed URL).
      // Open a new tab so the user can still save it manually, rather than
      // navigating away from the lesson.
      console.error("Image download failed, opening in a new tab:", err2);
      const opened = window.open(src, "_blank", "noopener,noreferrer");
      if (!opened) {
        toast?.error?.("Couldn't download the image ❌", { position: "top-center" });
      }
      return;
    }
  }

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke only after the browser has picked the download up.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

  toast?.success?.("Download successful!", { position: "top-center" });
};

const enhanceImage = (img, openImagePopup) => {
  if (img.closest(".img-wrapper")) return;

  const wrapper = document.createElement("div");
  wrapper.className = "img-wrapper";
  wrapper.style.cssText = `
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 800px;
  text-align: center;
  margin-top: 25px;
  `;


  img.parentNode.insertBefore(wrapper, img);
  wrapper.appendChild(img);

  // click-to-open
  img.style.cursor = "zoom-in";
  img.onclick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openImagePopup?.(img.src);
  };

  const btnContainer = document.createElement("div");
  btnContainer.style.cssText =
    "position:absolute;top:-25px;right:5px;display:flex;gap:6px;";

  const downloadBtn = createIconButton({
    iconSrc: download_Graph,
    title: "Download image",
    size: 22,
    onClick: (e) => {
      e.stopPropagation();
      downloadImage(img.currentSrc || img.src, img.alt || "");
    },
  });


  const copyBtn = createIconButton({
    iconSrc: content_copy_Graph,
    title: "Copy image",
    alt: "copy",
    size: 20,
    onClick: (e) => {
      e.stopPropagation();
      copyImageToClipboard(
        img.currentSrc || img.src,
        img.alt || "",
        getCopyableImageAttrs(img),
      );
    },
  });

  // const copyBtn = createIconButton({
  //   iconSrc: content_copy_Graph,
  //   alt: "copy",
  //   size: 20,
  //   onClick: (e) => {
  //     e.stopPropagation();
  //     copyToClipboard(`<br>${img.outerHTML}`);
  //   }
  // });

  btnContainer.append(copyBtn, downloadBtn);
  wrapper.appendChild(btnContainer);
};


// Inline SVGs for document card actions (no extra assets)
const downloadIconSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
const openInNewIconSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';

const getDocTypeFromHref = (href) => {
  try {
    const pathname = new URL(href, "https://dummy").pathname || "";
    const match = pathname.match(/\.([a-z0-9]+)$/i);
    const ext = (match && match[1]) || "";
    const upper = ext.toUpperCase();
    if (upper === "PDF") return { label: "PDF", color: "#e53935" };
    if (["PPT", "PPTX"].includes(upper)) return { label: "PPTX", color: "#f57c00" };
    if (["DOC", "DOCX"].includes(upper)) return { label: "DOC", color: "#1976d2" };
    if (["XLS", "XLSX"].includes(upper)) return { label: "XLSX", color: "#21A366" };
    return { label: upper || "FILE", color: "#5f6368" };
  } catch {
    return { label: "DOC", color: "#5f6368" };
  }
};

const getFilenameFromHref = (href) => {
  try {
    const pathname = new URL(href, "https://dummy").pathname || "";
    const segments = pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1] || "document";
    return decodeURIComponent(last);
  } catch {
    return "document";
  }
};



/**
 * Inserts an empty Lexical paragraph before every graph-image <img>,
 * so the image always starts on its own line in the editor.
 * Skips images that already have the empty paragraph right before them,
 * so it's safe to run even if the source already added one.
 */
function addBreakBeforeGraphImages(html) {
  // if (!html) return html;

  // // matches <img ...> whose class attribute contains the graph-image token
  // const graphImgRegex =
  //   /<img[^>]*class\s*=\s*['"][^'"]*\bgraph-image\b[^'"]*['"][^>]*>/gi;

  // return html.replace(graphImgRegex, (imgTag, offset, full) => {
  //   const before = full.slice(0, offset);
  //   // guard against double-insertion
  //   if (/<p class="editor-paragraph"><br\s*\/?><\/p>\s*$/i.test(before)) {
  //     return imgTag;
  //   }
  //   return `<p class="editor-paragraph"><br></p> ${imgTag}`;
  // });
  if (!html) return html;

  const escapeAttr = (value) =>
    String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;");

  return html.replace(/<img\b[^>]*>/gi, (imgTag, offset, full) => {
    const src = imgTag.match(/\bsrc\s*=\s*["']([^"']*)["']/i)?.[1];
    if (!src) return imgTag; // malformed tag — leave it alone rather than dropping it

    const alt = imgTag.match(/\balt\s*=\s*["']([^"']*)["']/i)?.[1] ?? "";

    // guard against double-insertion if a break is already there
    const before = full.slice(0, offset);
    const hasBreak = /<p class="editor-paragraph"><br\s*\/?><\/p>\s*$/i.test(before);

    const img = `<img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}" />`;
    return hasBreak ? img : `<p class="editor-paragraph"><br></p>${img}`;
  });
}

/**
 * Wraps a document download link in a card-style UI: header (type badge + filename + actions), body (title + subtitle).
 * @param {HTMLAnchorElement} anchor - The <a> element (e.g. download_link or PDF link)
 */
const enhanceDocumentLink = (anchor, openFileFn) => {
  if (anchor.closest(".document-link-card")) return;
  const href = anchor.getAttribute("href");
  if (!href) return;
  const docType = getDocTypeFromHref(href);
  const filename = anchor.textContent.trim();
  const displayFilename = filename.toUpperCase();
  const titleName = filename.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]/g, " ") || "Document";

  const card = document.createElement("div");
  card.className = "document-link-card";
  card.style.cssText = `
    display: block;
    min-width: 280px;
    max-width: 300px;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    margin: 12px 0;
    background: #fff;
  `;

  const header = document.createElement("div");
  header.style.cssText = `
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: linear-gradient(135deg, #37474f 0%, #263238 100%);
    color: #fff;
  `;

  const typeBadge = document.createElement("div");
  typeBadge.style.cssText = `
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: ${docType.color};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  typeBadge.textContent = docType.label;

  const nameSpan = document.createElement("span");
  nameSpan.style.cssText = `
    flex: 1;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;
  nameSpan.textContent = displayFilename;

  const actions = document.createElement("div");
  actions.style.cssText = `
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  `;

  const downloadBtn = document.createElement("button");
  downloadBtn.type = "button";
  downloadBtn.style.cssText = `
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #fff;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  `;
  downloadBtn.innerHTML = downloadIconSvg;
  downloadBtn.title = "Download";
  downloadBtn.onclick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    link.click();
    // toast?.success?.("Download started", { position: "top-center" });
  };

  // const openBtn = document.createElement("button");
  // openBtn.type = "button";
  // openBtn.style.cssText = downloadBtn.style.cssText;
  // openBtn.innerHTML = openInNewIconSvg;
  // openBtn.title = "Open in new tab";
  // openBtn.onclick = (e) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   window.open(href, "_blank", "noopener,noreferrer");
  //   // openFileFn({preview_url : href, download_url:href, extension:docType?.label, name: displayFilename})
  // };

  actions.append(downloadBtn);
  header.append(typeBadge, nameSpan, actions);

  const body = document.createElement("div");
  body.style.cssText = `
    padding: 2rem;
    background: #fff;
    text-align: center;
  `;

  const title = document.createElement("div");
  title.style.cssText = `
    font-size: 14px;
    font-weight: 700;
    color: #212121;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-bottom: 4px;
  `;
  title.textContent = titleName;

  const subtitle = document.createElement("div");
  subtitle.style.cssText = `
    font-size: 12px;
    color: #757575;
    font-weight: 400;
  `;
  // subtitle.textContent = "Click to open";

  body.append(title, subtitle);
  card.append(header, body);

  card.style.cursor = "pointer";

  const handleCardClick = (e) => {
    if (e.target.closest("button")) return;
    e.stopPropagation();
    e.preventDefault();
    openFileFn({
      preview_url: href,
      download_url: href,
      extension: docType.label,
      name: displayFilename,
    });
  };
  const handleMouseEnter = () => {
    card.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
  };
  const handleMouseLeave = () => {
    card.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
  };

  card.addEventListener("click", handleCardClick);
  card.addEventListener("mouseenter", handleMouseEnter);
  card.addEventListener("mouseleave", handleMouseLeave);

  anchor.replaceWith(card);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach(() => {
      if (!document.contains(card)) {
        card.removeEventListener("click", handleCardClick);
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
        observer.disconnect();
      }
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let formattedTime = '';

  if (hours > 0) {
    // Format for hours, minutes, and seconds
    formattedTime += `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}hrs`;
  } else if (minutes > 0) {
    // Format for minutes and seconds
    formattedTime += `${minutes}:${remainingSeconds.toString().padStart(2, '0')}min`;
  } else {
    // Format for seconds only
    formattedTime += `${remainingSeconds}s`;
  }

  return formattedTime;
}
function formatTimeAssignment(seconds) {
  const hours = seconds ? Math.floor(seconds / 3600) : '00';
  const minutes = seconds ? Math.floor((seconds % 3600) / 60) : '00';
  const remainingSeconds = seconds ? seconds % 60 : '00';

  let formattedTime = '';

  formattedTime += `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;

  return formattedTime;
}
function addFormattedTime(startTime, formattedTime) {
  // Parse the start time
  let [hours, minutes, seconds] = startTime.split(/[:\s]/);
  hours = hours % 12 + (startTime.includes('PM') ? 12 : 0);

  // Create a date object from the start time
  let startDate = new Date();
  startDate.setHours(hours, minutes, seconds, 0);

  // Calculate the additional seconds from formattedTime
  let additionalSeconds = 0;
  if (formattedTime.endsWith('min')) {
    let [addMinutes, addSeconds] = formattedTime.replace('min', '').split(':').map(Number);
    additionalSeconds = addMinutes * 60 + addSeconds;
  } else if (formattedTime.endsWith('s')) {
    additionalSeconds = parseInt(formattedTime);
  }

  // Add the duration to the start date
  startDate.setSeconds(startDate.getSeconds() + additionalSeconds);

  // Format the new time
  let newHours = startDate.getHours();
  let newMinutes = startDate.getMinutes().toString().padStart(2, '0');
  let newSeconds = startDate.getSeconds().toString().padStart(2, '0');
  let newTime = `${newHours % 12 || 12}:${newMinutes}:${newSeconds} ${newHours >= 12 ? 'PM' : 'AM'}`;

  return newTime;
}
const preprocessLatex = (latexString) => {
  return processLatex(latexString);
};
function escapeHtmlEntities(text) {
  if (text === "") {
    return text; // Return empty string as is
  }
  return text
    ?.replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/_/g, "&#95;"); // Escape underscores
}

const loadingMessages = [
  "Processing the request...",
  "Validating the input...",
  "Preparing the environment...",
  "Compiling the data...",
  "Analyzing the parameters...",
  "Retrieving the necessary resources...",
  "Running the calculations...",
  "Building the results...",
  "Sifting through the data...",
  "Checking the status...",
  "Executing the query...",
  "Optimizing the response...",
  "Cross-referencing the data...",
  "Fetching the required files...",
  "Synchronizing the data...",
  "Processing the transformation...",
  "Examining the request...",
  "Loading the required components...",
  "Gathering necessary details...",
  "Generating the output..."
]


export {
  useOutsideClick,
  getKeyByValue,
  getKeyByDictionary,
  getIdsForLanguages,
  isLinkOrText,
  capitalizeFirstLetter,
  capitalizeWordsInList,
  toTitleCase,
  parseFreeDomains,
  hasFreeDomainAccess,
  binaryStringToArrayBuffer,
  changeScrollPopup,
  convertSecondsToMinutes,
  truncate,
  getTitle,
  isOthersGender,
  timeDifferenceInMinutes,
  addIdsToParagraphs,
  generateIdFromHeader,
  formatTime,
  addFormattedTime,
  formatTimeAssignment,
  preprocessLatex,
  escapeHtmlEntities,
  enhanceImage,
  enhanceDocumentLink,
  createIconButton,
  copyImageToClipboard,
  downloadImage,
  getCopyableImageAttrs,
  _floatingTooltip,
  loadingMessages,
  addBreakBeforeGraphImages
};
